import { useEffect, useMemo, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ModelosAgrupados } from "@/components/ModelosAgrupados";
import { Target } from "lucide-react";

export const AlcanceGeneracionDialog = ({ maestraId, maestraNombre, preseleccion, open, onClose, onConfirm }) => {
  const [cargando, setCargando] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [modelosSel, setModelosSel] = useState([]);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    if (!open || !maestraId) return;
    setBusca("");
    setCargando(true);
    api.get(`/plantillas/${maestraId}/modelos-generables`)
      .then(({ data }) => {
        const lista = data || [];
        setModelos(lista);
        if (preseleccion && preseleccion.length) {
          setModelosSel(preseleccion.filter((id) => lista.some((m) => m.id === id)));
        } else {
          setModelosSel([]);
        }
      })
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [open, maestraId, preseleccion]);

  const modelosFiltrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return t ? modelos.filter((m) => (m.nombre_mostrar || "").toLowerCase().includes(t)) : modelos;
  }, [modelos, busca]);

  const continuar = () => {
    if (modelosSel.length === 0) {
      toast.error("Elegí al menos un modelo para generar");
      return;
    }
    onConfirm(modelosSel);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="alcance-ia-dialog" className="bg-white max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Target size={18} className="text-indigo-600" /> Alcance de la generación — {maestraNombre}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Elegí a qué modelos aplicar la generación con IA.</p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando modelos con catálogo…</p>
          ) : modelos.length === 0 ? (
            <p data-testid="alcance-sin-modelos" className="py-8 text-center text-sm text-muted-foreground">
              No hay otros modelos con catálogo de referencia para generar.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" data-testid="alcance-seleccionar-todos" className="h-7 text-xs"
                  onClick={() => setModelosSel(modelos.map((m) => m.id))}>
                  Seleccionar todos ({modelos.length})
                </Button>
                <Button variant="outline" size="sm" data-testid="alcance-ninguno" className="h-7 text-xs"
                  onClick={() => setModelosSel([])}>
                  Ninguno
                </Button>
              </div>
              <Input data-testid="alcance-buscar-modelo" value={busca} onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar modelo…" className="h-8" />
              <ModelosAgrupados modelos={modelosFiltrados} seleccionados={modelosSel} onChange={setModelosSel}
                testPrefix="alcance-modelo" emptyMsg="Sin resultados para la búsqueda." />
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 items-center sm:justify-between">
          <span className="text-xs text-muted-foreground" data-testid="alcance-total">
            {modelos.length > 0 && `${modelosSel.length} modelo(s) objetivo`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="alcance-cancelar" onClick={onClose}>Cancelar</Button>
            <Button data-testid="alcance-continuar" onClick={continuar}
              disabled={cargando || modelos.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">Continuar</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
